import React, { useEffect, useState } from "react";
import { useLang } from "@/context/LangContext";
import api from "@/lib/api";
import { Award, Globe2, X, ArrowRight, GraduationCap, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

const STAFF_TYPE_LABEL = {
  teacher: { en: "Teacher", zh: "教师" },
  assistant_teacher: { en: "Assistant Teacher", zh: "助教" },
};
const EDU_LABEL = {
  high_school: { en: "High School", zh: "高中" }, bachelor: { en: "Bachelor's", zh: "学士" },
  master: { en: "Master's", zh: "硕士" }, phd: { en: "Ph.D.", zh: "博士" },
};

function initials(name) {
  if (!name) return "?";
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map(s => s[0].toUpperCase()).join("");
}

function TeacherModal({ teacher, lang, onClose }) {
  useEffect(() => {
    function onKey(e) { if (e.key === "Escape") onClose(); }
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [onClose]);

  function requestBooking() {
    try {
      sessionStorage.setItem("requested_teacher", teacher.name_en);
    } catch (_e) { /* ignore */ }
    onClose();
    // Use a brief timeout so the modal close animation feels natural before scroll.
    setTimeout(() => {
      window.location.hash = "#booking";
      window.dispatchEvent(new CustomEvent("requested-teacher-change", { detail: teacher.name_en }));
    }, 80);
  }

  if (!teacher) return null;
  const typ = STAFF_TYPE_LABEL[teacher.staff_type] || STAFF_TYPE_LABEL.teacher;
  const displayName = lang === "zh" && teacher.name_zh ? teacher.name_zh : teacher.name_en;

  return (
    <div data-testid="teacher-modal-overlay" onClick={onClose}
      className="fixed inset-0 z-[60] bg-black/55 backdrop-blur-sm flex items-center justify-center p-4 md:p-8">
      <div data-testid="teacher-modal" onClick={(e)=>e.stopPropagation()}
        className="relative bg-[hsl(var(--background))] rounded-2xl max-w-4xl w-full max-h-[88vh] overflow-y-auto shadow-2xl grid grid-cols-1 md:grid-cols-12">
        <button onClick={onClose} data-testid="teacher-modal-close"
          className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-white/95 hover:bg-white border border-[hsl(var(--border))] flex items-center justify-center text-[hsl(var(--foreground))]/70 hover:text-[hsl(var(--foreground))]">
          <X className="w-4 h-4" />
        </button>
        <div className="md:col-span-5 bg-[hsl(var(--muted))] relative aspect-square md:aspect-auto md:min-h-[480px]">
          {teacher.photo_url ? (
            <img src={teacher.photo_url} alt={teacher.name_en} className="absolute inset-0 w-full h-full object-cover" />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-[hsl(var(--primary))]/20 to-[hsl(var(--secondary))]/15">
              <span className="font-display text-[140px] text-[hsl(var(--primary))]/55 leading-none">{initials(teacher.name_en)}</span>
            </div>
          )}
          <div className="absolute bottom-4 left-4 right-4 flex flex-wrap gap-1.5">
            <span className="text-[10px] uppercase tracking-[0.22em] px-2.5 py-1 rounded-full bg-white/95 text-[hsl(var(--primary))] backdrop-blur">{typ[lang === "zh" ? "zh" : "en"]}</span>
            <span className="text-[10px] uppercase tracking-[0.22em] px-2.5 py-1 rounded-full bg-white/95 text-[hsl(var(--secondary))] backdrop-blur flex items-center gap-1"><Globe2 className="w-3 h-3" />{teacher.nationality}</span>
          </div>
        </div>
        <div className="md:col-span-7 p-7 md:p-10 space-y-5">
          <div>
            <p className="text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--primary))]">{lang === "zh" ? "我们的老师" : "Our Teacher"}</p>
            <h2 className="font-display text-3xl md:text-4xl tracking-tight font-medium mt-2">{displayName}</h2>
            {lang !== "zh" && teacher.name_zh && <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">{teacher.name_zh}</p>}
            <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))] flex items-center gap-1.5"><GraduationCap className="w-3.5 h-3.5" />{EDU_LABEL[teacher.education_level]?.[lang === "zh" ? "zh" : "en"] || ""}</p>
          </div>
          {teacher.public_bio && (
            <div>
              <p className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))]">{lang === "zh" ? "简介" : "About"}</p>
              <p className="text-sm md:text-[15px] text-[hsl(var(--foreground))]/90 mt-2 leading-relaxed whitespace-pre-wrap">{teacher.public_bio}</p>
            </div>
          )}
          {(teacher.teaching_certs?.length || teacher.language_certs?.length) > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))]">{lang === "zh" ? "资格与证书" : "Qualifications"}</p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {(teacher.teaching_certs || []).map(c => (
                  <span key={c} className="px-2.5 py-1 text-xs uppercase tracking-wider rounded-full bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] border border-[hsl(var(--primary))]/30 flex items-center gap-1"><Award className="w-3 h-3" />{c.replace("_", " ")}</span>
                ))}
                {(teacher.language_certs || []).map(c => (
                  <span key={`l-${c}`} className="px-2.5 py-1 text-xs uppercase tracking-wider rounded-full bg-[hsl(var(--secondary))]/10 text-[hsl(var(--secondary))] border border-[hsl(var(--secondary))]/30">{c}</span>
                ))}
              </div>
            </div>
          )}
          <div className="pt-3 border-t border-[hsl(var(--border))]">
            <Button onClick={requestBooking} data-testid="teacher-modal-request-booking"
              className="w-full sm:w-auto rounded-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white px-6">
              <Sparkles className="w-4 h-4 mr-1.5" />
              {lang === "zh" ? `请求与 ${displayName} 老师上课` : `Request a trial with ${displayName}`}
              <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
            <p className="text-xs text-[hsl(var(--muted-foreground))] mt-2 italic">
              {lang === "zh"
                ? "我们会将您的偏好备注在课程申请中，正式排课时尽量满足。"
                : "We'll note your preference on the booking and do our best to honour it when scheduling."}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function TeacherCard({ t, lang, onOpen }) {
  const typ = STAFF_TYPE_LABEL[t.staff_type] || STAFF_TYPE_LABEL.teacher;
  const displayName = lang === "zh" && t.name_zh ? t.name_zh : t.name_en;
  return (
    <button type="button" onClick={()=>onOpen(t)} data-testid={`teacher-card-${t.name_en.replace(/\s+/g, "-").toLowerCase()}`}
      className="group relative text-left bg-white rounded-2xl border border-[hsl(var(--border))] overflow-hidden hover:shadow-[0_20px_50px_-30px_rgba(60,40,20,0.35)] hover:border-[hsl(var(--primary))]/40 transition-all focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]/30 focus:ring-offset-2">
      <div className="aspect-[4/5] bg-[hsl(var(--muted))] relative overflow-hidden">
        {t.photo_url ? (
          <img src={t.photo_url} alt={t.name_en} className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" loading="lazy" />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-[hsl(var(--primary))]/15 to-[hsl(var(--secondary))]/10">
            <span className="font-display text-6xl text-[hsl(var(--primary))]/60">{initials(t.name_en)}</span>
          </div>
        )}
        <span className="absolute top-3 left-3 text-[10px] uppercase tracking-[0.22em] px-2.5 py-1 rounded-full bg-white/95 text-[hsl(var(--primary))] backdrop-blur">
          {typ[lang === "zh" ? "zh" : "en"]}
        </span>
        <span className="absolute bottom-3 right-3 text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded-full bg-white/0 text-white opacity-0 group-hover:bg-white/95 group-hover:text-[hsl(var(--primary))] group-hover:opacity-100 transition-all flex items-center gap-1">
          {lang === "zh" ? "查看详情" : "View profile"} <ArrowRight className="w-3 h-3" />
        </span>
      </div>
      <div className="p-5">
        <div className="flex items-baseline justify-between gap-2">
          <h3 className="font-display text-xl tracking-tight">{displayName}</h3>
          <span className="text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] flex items-center gap-1"><Globe2 className="w-3 h-3" />{t.nationality}</span>
        </div>
        <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">{EDU_LABEL[t.education_level]?.[lang === "zh" ? "zh" : "en"] || ""}</p>
        {t.public_bio && <p className="text-sm text-[hsl(var(--foreground))]/85 mt-3 leading-relaxed line-clamp-4">{t.public_bio}</p>}
        <div className="mt-4 flex flex-wrap gap-1.5">
          {(t.teaching_certs || []).map(c => (
            <span key={c} className="px-2 py-0.5 text-[10px] uppercase tracking-wider rounded-full bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] border border-[hsl(var(--primary))]/30 flex items-center gap-1">
              <Award className="w-2.5 h-2.5" />{c.replace("_", " ")}
            </span>
          ))}
          {(t.language_certs || []).map(c => (
            <span key={`l-${c}`} className="px-2 py-0.5 text-[10px] uppercase tracking-wider rounded-full bg-[hsl(var(--secondary))]/10 text-[hsl(var(--secondary))] border border-[hsl(var(--secondary))]/30">{c}</span>
          ))}
        </div>
      </div>
    </button>
  );
}

export default function TeachersSection() {
  const { lang } = useLang();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openTeacher, setOpenTeacher] = useState(null);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/public/teachers"); setItems(data); }
      catch (_e) { /* silent — section just won't render */ }
      finally { setLoading(false); }
    })();
  }, []);

  if (!loading && items.length === 0) return null;

  return (
    <section id="teachers" data-testid="teachers-section" className="py-20 md:py-28 bg-[hsl(var(--background))]">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="max-w-2xl">
          <p className="text-xs uppercase tracking-[0.28em] text-[hsl(var(--primary))]">{lang === "zh" ? "我们的师资团队" : "Meet our teachers"}</p>
          <h2 className="font-display text-4xl md:text-5xl tracking-tight font-medium mt-3 leading-tight">
            {lang === "zh"
              ? <>认真、有趣，<em className="italic text-[hsl(var(--primary))]" style={{ fontStyle: "italic" }}>始终如一</em>。</>
              : <>Thoughtful, fun, and <em className="italic text-[hsl(var(--primary))]" style={{ fontStyle: "italic" }}>genuinely consistent</em>.</>}
          </h2>
          <p className="mt-3 text-sm md:text-base text-[hsl(var(--muted-foreground))] max-w-xl">
            {lang === "zh"
              ? "我们用心挑选每一位老师 — 国际背景、专业证书与多年教学经验。点击老师卡片查看完整简介。"
              : "Every teacher on our team is hand-picked — international backgrounds, recognised certifications, and years of classroom experience. Tap a card to see their full profile."}
          </p>
        </div>
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {items.map((t, idx) => <TeacherCard key={t.name_en + idx} t={t} lang={lang} onOpen={setOpenTeacher} />)}
        </div>
      </div>
      {openTeacher && <TeacherModal teacher={openTeacher} lang={lang} onClose={()=>setOpenTeacher(null)} />}
    </section>
  );
}
